import React, { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import dayjs from 'dayjs'
import 'dayjs/locale/sv'
import 'dayjs/locale/en'
import 'dayjs/locale/nb'
import weekday from 'dayjs/plugin/weekday'
import GrooverText from './GrooverText'
import { Language } from '../../../pure-js/types/Antiloop'

dayjs.extend(weekday)

interface PrivateLessonCalendarProps {
  selectedDate: string | null
  onDateSelect: (date: string) => void
  availableDates?: string[] // ISO date strings
  language?: Language
}

const PrivateLessonCalendar: React.FC<PrivateLessonCalendarProps> = ({
  selectedDate,
  onDateSelect,
  availableDates,
  language
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs())

  useEffect(() => {
    const locale = language === 'no' ? 'nb' : language || 'sv'
    dayjs.locale(locale)
  }, [language])

  // Generate available dates if not provided (mock: today + next 30 days)
  const mockAvailableDates = useMemo(() => {
    const dates: string[] = []
    for (let i = 0; i < 30; i++) {
      dates.push(dayjs().add(i, 'day').format('YYYY-MM-DD'))
    }
    return dates
  }, [])

  const datesToShow = availableDates || mockAvailableDates

  const monthStart = currentMonth.startOf('month')
  const monthEnd = currentMonth.endOf('month')

  // Get the day of week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = monthStart.day()

  // Calculate how many days to go back to reach Monday
  // If firstDayOfWeek is 0 (Sunday), go back 6 days to Monday
  // If firstDayOfWeek is 1 (Monday), go back 0 days
  // If firstDayOfWeek is 2 (Tuesday), go back 1 day to Monday, etc.
  const daysToGoBack = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
  const startDate = monthStart.subtract(daysToGoBack, 'day')

  // Calculate the last day of the week containing the last day of the month
  const lastDayOfWeek = monthEnd.day()
  const daysToGoForward = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek
  const endDate = monthEnd.add(daysToGoForward, 'day')

  const days: dayjs.Dayjs[] = []
  let day = startDate
  while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
    days.push(day)
    day = day.add(1, 'day')
  }

  const isAvailable = (date: dayjs.Dayjs) => {
    return datesToShow.includes(date.format('YYYY-MM-DD'))
  }

  const isSelected = (date: dayjs.Dayjs) => {
    return selectedDate === date.format('YYYY-MM-DD')
  }

  const isToday = (date: dayjs.Dayjs) => {
    return date.isSame(dayjs(), 'day')
  }

  const isPast = (date: dayjs.Dayjs) => {
    return date.isBefore(dayjs(), 'day')
  }

  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'))
  }

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'))
  }

  const weekDays =
    language === 'sv'
      ? ['Må', 'Ti', 'On', 'To', 'Fr', 'Lö', 'Sö']
      : language === 'no'
        ? ['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø']
        : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  return (
    <CalendarContainer>
      <CalendarHeader>
        <NavButton onClick={handlePreviousMonth}>
          <ArrowBackIos sx={{ fontSize: 16 }} />
        </NavButton>
        <MonthYear>{currentMonth.format('MMMM YYYY')}</MonthYear>
        <NavButton onClick={handleNextMonth}>
          <ArrowForwardIos sx={{ fontSize: 16 }} />
        </NavButton>
      </CalendarHeader>

      <WeekDays>
        {weekDays.map((day) => (
          <WeekDay key={day}>{day}</WeekDay>
        ))}
      </WeekDays>

      <DaysGrid>
        {days.map((day, index) => {
          const available = isAvailable(day)
          const selected = isSelected(day)
          const today = isToday(day)
          const past = isPast(day)

          return (
            <DayCell
              key={index}
              $isAvailable={available && !past}
              $isSelected={selected}
              $isToday={today}
              $isPast={past}
              onClick={() => {
                if (available && !past) {
                  onDateSelect(day.format('YYYY-MM-DD'))
                }
              }}
            >
              {day.date()}
            </DayCell>
          )
        })}
      </DaysGrid>

      <Legend>
        <LegendItem>
          <LegendDot $color="#7c3aed" />
          <LegendText>
            <GrooverText textKey="privateLessonsCheckoutAvailableDates" language={language} />
          </LegendText>
        </LegendItem>
      </Legend>
    </CalendarContainer>
  )
}

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  padding: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #000;
  }
`

const MonthYear = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000;
`

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`

const WeekDay = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  padding: 8px 0;
`

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`

const DayCell = styled.div<{
  $isAvailable: boolean
  $isSelected: boolean
  $isToday: boolean
  $isPast: boolean
}>`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: ${({ $isAvailable, $isPast }) => ($isAvailable && !$isPast ? 'pointer' : 'not-allowed')};
  font-size: 14px;
  font-weight: ${({ $isSelected, $isToday }) => ($isSelected || $isToday ? 600 : 400)};
  color: ${({ $isSelected, $isPast, $isAvailable }) => {
    if ($isSelected) return 'white'
    if ($isPast) return '#ccc'
    if ($isAvailable) return '#000'
    return '#ccc'
  }};
  background-color: ${({ $isSelected, $isToday }) => {
    if ($isSelected) return '#7c3aed'
    if ($isToday) return '#f0f0f0'
    return 'transparent'
  }};
  text-decoration: ${({ $isAvailable, $isPast }) => ($isAvailable && !$isPast ? 'underline' : 'none')};
  text-decoration-color: #7c3aed;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ $isAvailable, $isPast, $isSelected }) => {
      if ($isSelected) return '#7c3aed'
      if ($isAvailable && !$isPast) return '#f8f4ff'
      return 'transparent'
    }};
  }
`

const Legend = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LegendDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`

const LegendText = styled.span`
  font-size: 12px;
  color: #666;
`

export default PrivateLessonCalendar
